import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Card, CardContent, CardHeader, CardTitle } from '@mdvip/shared/components/ui/card';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useLocale } from '@mdvip/shared/stores/userPreferencesStore';
import { useTranslation } from 'react-i18next';
import type { PracticeAddress, PracticeInfo, PracticeOfficeHours } from '@/hooks/practice/practiceApi';
import { groupOfficeHours } from '@/lib/practice/practice.format';

interface PracticeOfficeCardProps {
  practice: PracticeInfo;
}

/**
 * The comma between the street lines is a deliberate departure from the design, which models the
 * street as one space-joined field: `ProfileDetailsCard` prints the member's own address with a
 * comma, and both render on this page at once.
 */
function addressLines(address: PracticeAddress): string[] {
  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const locality = [address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [street, locality].filter(Boolean);
}

function OfficeAddress({ address }: { address: PracticeAddress | null }) {
  const lines = address ? addressLines(address) : [];
  if (lines.length === 0) {
    return null;
  }
  return (
    <address className="text-sm not-italic leading-relaxed text-foreground">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </address>
  );
}

function OfficeHoursList({ officeHours }: { officeHours: PracticeOfficeHours }) {
  const { t } = useTranslation('members');
  const locale = useLocale();

  return (
    <dl className="flex flex-col gap-1">
      {groupOfficeHours(officeHours, locale).map((row) => (
        <div key={row.from} className="flex justify-between gap-4 text-sm">
          <dt className="text-muted-foreground">
            {row.from === row.to
              ? t(`profile.physicianOffice.dayShort.${row.from}`)
              : t('profile.physicianOffice.dayRange', {
                  from: t(`profile.physicianOffice.dayShort.${row.from}`),
                  to: t(`profile.physicianOffice.dayShort.${row.to}`),
                })}
          </dt>
          {/* nowrap: at rail width a flexible column orphaned "PM" onto its own line. */}
          <dd className="shrink-0 whitespace-nowrap text-foreground">
            {row.closed ? t('profile.physicianOffice.closed') : row.hours}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function PracticeOfficeCard({ practice }: PracticeOfficeCardProps) {
  const { t } = useTranslation('members');

  return (
    <Card className="gap-4" data-slot="card-physician-office">
      <CardHeader>
        <CardTitle className="text-sm">{t('profile.physicianOffice.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <OfficeAddress address={practice.address} />
        {practice.officeHours && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Icon name="clock" size="xs" className="text-muted-foreground" />
              {t('profile.physicianOffice.hoursTitle')}
            </div>
            <OfficeHoursList officeHours={practice.officeHours} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PracticeOfficeCardSkeleton() {
  const { t } = useTranslation('members');

  return (
    <Card className="gap-4" data-slot="card-physician-office-skeleton" aria-busy="true">
      <CardHeader>
        <CardTitle className="text-sm">{t('profile.physicianOffice.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}
